// Post-processing for stored article HTML rendered read-only in the article
// body. Routes raster images on our media host through gow-media-worker for
// resizing + srcset and applies fetchpriority/lazy loading — all without
// changing the tags, classes, or layout the content was authored with.
import { resolveImageSrc } from "./image";
import { isMediaUrl, mediaOriginal, mediaSrc } from "./imageLoader";
import { normalizeStoredArticleHtml } from "./articleNormalize";

/** Intrinsic pixel size per normalised image URL (see utils/imageDimensions.ts). */
export type ImageDimensions = Record<string, { width: number; height: number }>;

// Subset of gow-media-worker's width ladder, so nothing snaps upward on arrival.
// 750 dropped from the old list: it sits one step from 828 and every rung here is
// a separately billed transformation.
//
// Exported because pages/article/[id].tsx preloads the LCP body image and has to
// emit the *same* candidates — a preload whose srcset does not match the rendered
// <img> downloads the image twice. Keeping one list makes that structural rather
// than a comment asking the next person to remember.
export const ARTICLE_BODY_WIDTHS = [640, 828, 1080, 1200];
const RASTER_RE = /\.(jpe?g|png|webp)(\?|#|$)/i;

// Normalize the stored src first: resolveImageSrc decodes the %2F-encoded object
// paths the CMS wrote, and turns bare host/path values into absolute URLs, which
// is what decides whether isMediaUrl recognises this as ours.
const normalize = (src: string): string => resolveImageSrc(src) || src;

const buildSrcSet = (src: string): string =>
  ARTICLE_BODY_WIDTHS.map((w) => `${mediaSrc(src, w)} ${w}w`).join(", ");

const getAttr = (tag: string, name: string): string | null => {
  const m = tag.match(new RegExp(`\\b${name}="([^"]*)"`, "i"));
  return m ? m[1] : null;
};

// Some article bodies carry a pasted optimiser URL — `/_next/image?url=…` (or the
// absolute form on any host) — instead of the image itself. The edge now 403s
// every `/_next/image` request whose `url` is not our media host and 302s the
// rest, so unwrap them here: take the `url` param, decode it once (Next built it
// with one encodeURIComponent; our object keys keep their inner `%2F`, which the
// media worker decodes itself) and treat the result as the authored src. Tiptap
// serialises `&` between attribute query params as `&amp;`. Anything that is not
// a wrapper, or a wrapper we cannot decode, is returned untouched.
const NEXT_IMAGE_RE = /^(?:https?:\/\/[^/]+)?\/_next\/image\?(.*)$/i;
const unwrapNextImage = (src: string | null): string | null => {
  if (!src) return src;
  const m = src.match(NEXT_IMAGE_RE);
  if (!m) return src;
  const param = m[1].split(/&(?:amp;)?/).find((p) => p.startsWith("url="));
  if (!param) return src;
  try {
    const decoded = decodeURIComponent(param.slice("url=".length));
    // Only absolute URLs go through resolveImageSrc: it prefixes
    // BACKEND_BASE_URL to root-relative paths, which would send a wrapped
    // frontend asset (/_next/static/..., /GamesOn_Logo.jpg) to the API host.
    return (/^https?:\/\//i.test(decoded) ? normalize(decoded) : decoded) || src;
  } catch {
    return src;
  }
};

// Only our own media is rewritten. A third-party URL is left exactly as authored
// (or, for an unwrapped `/_next/image` wrapper, as its decoded URL): building a
// srcset for it would emit four identical URLs, and the only way to actually
// resize it would be to proxy and re-encode it on our bill.
const shouldOptimize = (src: string | null): src is string => {
  if (!src) return false;
  if (src.startsWith("data:") || src.startsWith("blob:")) return false;
  if (!/^https?:\/\//i.test(src)) return false;
  if (!RASTER_RE.test(src)) return false;
  return isMediaUrl(normalize(src));
};

// One spelling per object for the dimensions map: the plain (never resized) URL.
// Both the prober and the renderer key on it, so `%2F` and `/` spellings of one
// key match, whatever `mediaSrc` emits for the displayed candidates.
const mediaKey = (normalized: string): string => mediaOriginal(normalized);

/**
 * The body images whose intrinsic size is worth probing: our own raster media,
 * deduplicated, in document order. Same predicate `processArticleImages` uses to
 * decide what to rewrite, so every probed URL is one that will get attributes.
 */
export const collectArticleImageUrls = (html: string): string[] => {
  const urls: string[] = [];
  for (const m of html.matchAll(/<img\b([^>]*)>/gi)) {
    const src = unwrapNextImage(getAttr(m[0], "src"));
    if (!shouldOptimize(src)) continue;
    const key = mediaKey(normalize(src));
    if (!urls.includes(key)) urls.push(key);
  }
  return urls;
};

/**
 * Stored article HTML → the markup the page renders, on server and client alike.
 * Deterministic for one input, so getStaticProps and the hydrating component
 * produce byte-identical strings and React never has to reconcile the body.
 */
export const renderArticleBody = (contentBody: string, dimensions?: ImageDimensions): string =>
  processArticleImages(normalizeStoredArticleHtml(contentBody), dimensions);

/**
 * Convert a YouTube embed URL to the privacy-enhanced youtube-nocookie.com
 * domain, which serves the same player without setting cookies until the user
 * actually plays the video. Non-YouTube / already-nocookie URLs are unchanged.
 */
export const toNoCookieYouTube = (url: string): string => {
  if (!url) return url;
  return url.replace(
    /https?:\/\/(?:www\.)?youtube\.com\/embed\//i,
    "https://www.youtube-nocookie.com/embed/"
  );
};

const YT_ID_RE =
  /(?:youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?v=|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;

// Build a click-to-load facade for a YouTube embed inside article-body HTML:
// a thumbnail with a play button, carrying the video id in data-yt-id. No
// iframe / player JS / cookies load until the reader clicks (see the click
// handler in ContentBodyHtml that swaps it for the real cookieless iframe).
const buildYouTubeFacade = (iframeTag: string): string | null => {
  const src = getAttr(iframeTag, "src");
  if (!src) return null;
  const m = src.match(YT_ID_RE);
  if (!m) return null;
  const id = m[1];
  // In article bodies the iframe is always wrapped in the Tiptap iframe node's
  // `<div class="aspect-w-16 aspect-h-9">`, which already reserves 16:9 space via
  // a padding hack and expects an absolutely-positioned child. So the facade
  // fills that parent with position:absolute; inset:0 (NOT its own aspect-ratio,
  // which would stack a second 16:9 box and leave a large empty gap below).
  return (
    `<div class="yt-facade" data-yt-id="${id}" role="button" tabindex="0" aria-label="Play video" ` +
    `style="position:absolute;top:0;left:0;width:100%;height:100%;cursor:pointer;` +
    `background-image:url(https://i.ytimg.com/vi_webp/${id}/hqdefault.webp),url(https://i.ytimg.com/vi/${id}/hqdefault.jpg);` +
    `background-size:cover;background-position:center;border-radius:0.5rem;overflow:hidden">` +
    `<span class="yt-facade__btn" style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);` +
    `width:68px;height:48px;background:rgba(33,33,33,0.8);border-radius:12px;display:flex;align-items:center;justify-content:center">` +
    `<svg viewBox="0 0 24 24" width="28" height="28" fill="#fff" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>` +
    `</span></div>`
  );
};

/**
 * Rewrite <img> and <iframe> tags in article body HTML:
 *  - unwrap pasted `/_next/image?url=…` src values to the image they wrap
 *  - route raster <img> src on our media host through gow-media-worker + srcset
 *  - give the first image fetchpriority="high" (LCP), the rest loading="lazy"
 *  - decoding="async" so a large body image never blocks the text paint
 *  - switch YouTube <iframe> embeds to the cookieless youtube-nocookie.com
 * Existing loading/fetchpriority/decoding/srcset/sizes attributes are preserved,
 * except a srcset of `/_next/image` candidates on an unwrapped tag (it would be
 * blocked).
 */
// On phones the body images are the page's bandwidth problem, and `loading="lazy"`
// does not solve it: Chrome's lazy-load distance threshold grows with a slow
// connection (several screens), so on a throttled 4G profile nearly every image
// in a long article starts downloading within the first 300 ms — ~5 MB in flight
// while the CSS, fonts and scripts are still arriving, and Lighthouse's model
// charges the text LCP for all of it. So below `md` the picture is held back
// behind a `<picture>` whose phone-only `<source>` is an empty SVG with the
// image's own aspect ratio (the box keeps its size); ContentBodyHtml removes
// that source once the image is within ~600px of the viewport and the browser
// then falls through to the real `src`. The `<img>` keeps its real src/srcset
// (crawlers, no-JS), and from `md` up the `<source>` never matches, so desktop
// behaviour — including the eager, preloaded LCP image — is exactly as before.
// Only images whose intrinsic size is known are deferred: without it the
// placeholder's 1:1 ratio would resize the box.
const PHONE_MEDIA = "(max-width: 767px)";
const placeholderFor = (dims: { width: number; height: number }): string =>
  `data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20viewBox%3D%270%200%20${dims.width}%20${dims.height}%27%3E%3C%2Fsvg%3E`;
const deferredOnPhones = (imgTag: string, dims: { width: number; height: number }): string =>
  `<picture data-defer=""><source media="${PHONE_MEDIA}" srcset="${placeholderFor(dims)}">${imgTag}</picture>`;

export const processArticleImages = (html: string, dimensions?: ImageDimensions): string => {
  let lcpDone = false;
  const withImages = html.replace(/<img\b([^>]*)>/gi, (full: string, attrs: string) => {
    const rawSrc = getAttr(full, "src");
    const src = unwrapNextImage(rawSrc);
    let out = attrs;
    let deferOnPhones: { width: number; height: number } | null = null;

    if (src !== rawSrc) {
      out = out.replace(/\bsrc="[^"]*"/i, `src="${src}"`);
      if (/\bsrcset="[^"]*\/_next\/image/i.test(out)) out = out.replace(/\s*\bsrcset="[^"]*"/i, "");
    }

    if (shouldOptimize(src)) {
      const normalized = normalize(src);
      if (!/\bsrcset=/i.test(out)) out += ` srcset="${buildSrcSet(normalized)}"`;
      if (!/\bsizes=/i.test(out)) out += ` sizes="(max-width: 768px) 100vw, 800px"`;
      out = out.replace(/\bsrc="[^"]*"/i, `src="${mediaSrc(normalized, 1080)}"`);
      // Intrinsic size so the browser reserves the box before the bytes arrive
      // (the body's share of CLS, and Lighthouse's "unsized images"). The
      // `.custom-image-*` rules and Tailwind's preflight both set `height: auto`,
      // so the attributes only fix the aspect ratio, never the rendered size.
      const dims = dimensions?.[mediaKey(normalized)];
      if (dims && !/\bwidth=/i.test(out) && !/\bheight=/i.test(out)) {
        out += ` width="${dims.width}" height="${dims.height}"`;
        deferOnPhones = dims;
      }
    }

    if (!/\bloading=/i.test(out) && !/\bfetchpriority=/i.test(out)) {
      if (!lcpDone) {
        lcpDone = true;
        out = ` fetchpriority="high"` + out;
      } else {
        out = ` loading="lazy"` + out;
      }
    }

    if (!/\bdecoding=/i.test(out)) out += ` decoding="async"`;

    const img = `<img${out}>`;
    return deferOnPhones ? deferredOnPhones(img, deferOnPhones) : img;
  });

  // YouTube iframes → click-to-load facade (no cookies/JS until played).
  // Other iframes → keep, just ensure lazy loading.
  return withImages.replace(/<iframe\b([^>]*)><\/iframe>|<iframe\b([^>]*)\/?>/gi, (full: string) => {
    if (/youtube(?:-nocookie)?\.com|youtu\.be/i.test(full)) {
      const facade = buildYouTubeFacade(full);
      if (facade) return facade;
    }
    // Non-YouTube iframe: preserve, ensure lazy.
    const attrs = full.replace(/^<iframe\b/i, "").replace(/><\/iframe>$|\/?>$/i, "");
    let out = attrs;
    if (!/\bloading=/i.test(out)) out += ` loading="lazy"`;
    return `<iframe${out}></iframe>`;
  });
};
